<script lang="ts">
  // BJL-CHECK: Button (used), Modal. The shell is a full-bleed fixed overlay that owns its
  // own safe-area padding and z-index (2000, above the detail sheets); bjl Modal centers a
  // sized dialog over a backdrop and cannot go edge-to-edge. The pan/zoom viewer has no bjl
  // counterpart at all. See artifacts/deviations/MapSheet.md.
  import { Button } from '@byjackli-labs/ui/Button'
  import { Colors } from '../../ui/colors'
  import { panelFrame, panelTitle } from '../../ui/theme'
  import { safeTop, safeBottom } from '../../ui/safeArea'

  /**
   * Full-screen overlay showing the official MTA subway map as a zoomable, pannable image.
   *
   * The map is rasterized from `images/MTA_map.pdf` at build time into a high-DPI WebP
   * (5184x6264, ~1.8 MB) at `apps/mobile/public/MTA_map.webp`. Vite serves it at the web
   * root and bundles it into `dist/`, which `cap sync` copies into the native builds. It's a
   * bundled web asset, so it's referenced by a plain root-relative URL: the native WebView
   * serves the app from its own local scheme, so `/MTA_map.webp` resolves against the bundle
   * on iOS, Android and web alike.
   *
   * Do NOT run this through `Capacitor.convertFileSrc` — that's for device-native paths.
   * On Android it rewrites a web-root path into the `_capacitor_file_` bridge, which
   * resolves against the device root and 404s, leaving the map blank.
   *
   * The pan/zoom layer is hand-rolled over pointer events (`react-zoom-pan-pinch` has no
   * Svelte equivalent — see the deviation note). Pure JS, no native bridge, so every gesture
   * behaves identically across web, WKWebView and Android System WebView — which is also
   * what let the map render inline on Android at all, where the PDF could not. The <img>
   * uses `object-fit: contain`, so scale 1 is "fit to screen" (MIN_SCALE).
   */

  const IMG_PATH = '/MTA_map.webp'

  const MIN_SCALE = 1
  const MAX_SCALE = 8
  /** Multiplier per double-tap and per wheel notch (rzpp's `step: 0.7` / `step: 0.2`). */
  const DOUBLE_TAP_FACTOR = 1.7
  const WHEEL_FACTOR = 1.2
  const DOUBLE_TAP_MS = 300
  const DOUBLE_TAP_SLOP_PX = 24

  interface Props {
    onClose: () => void
  }

  const { onClose }: Props = $props()

  let viewport: HTMLDivElement
  let scale = $state(MIN_SCALE)
  let tx = $state(0)
  let ty = $state(0)

  // Live pointers, so one finger pans and two pinch. `prev` carries the last frame's
  // midpoint and spread, so pinching applies incrementally: a two-finger drag pans and zooms
  // at once instead of snapping back to the gesture's start anchor.
  const pointers = new Map<number, { x: number; y: number }>()
  let prev: { x: number; y: number; dist: number } | null = null
  let lastTap: { t: number; x: number; y: number } | null = null

  function clamp(v: number, lo: number, hi: number) {
    return Math.min(hi, Math.max(lo, v))
  }

  /**
   * Keeps the scaled content covering the viewport. The content box is exactly viewport-sized
   * at scale 1, so the scaled extent is `scale * size`: larger than the viewport clamps the
   * offset to the overhang, and at scale 1 it centers — which is also what centers on init.
   */
  function setPan(nextScale: number, x: number, y: number) {
    const r = viewport.getBoundingClientRect()
    const spanX = r.width - nextScale * r.width
    const spanY = r.height - nextScale * r.height
    tx = spanX >= 0 ? spanX / 2 : clamp(x, spanX, 0)
    ty = spanY >= 0 ? spanY / 2 : clamp(y, spanY, 0)
  }

  /** Zooms to `nextScale` keeping the content point under (px, py) — viewport-relative — fixed. */
  function zoomAt(nextScale: number, px: number, py: number) {
    const next = clamp(nextScale, MIN_SCALE, MAX_SCALE)
    const ratio = next / scale
    const x = px - (px - tx) * ratio
    const y = py - (py - ty) * ratio
    scale = next
    setPan(next, x, y)
  }

  function local(e: { clientX: number; clientY: number }) {
    const r = viewport.getBoundingClientRect()
    return { x: e.clientX - r.left, y: e.clientY - r.top }
  }

  function centroid() {
    const pts = [...pointers.values()]
    const x = pts.reduce((a, p) => a + p.x, 0) / pts.length
    const y = pts.reduce((a, p) => a + p.y, 0) / pts.length
    const dist =
      pts.length < 2 ? 0 : Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y)
    return { x, y, dist }
  }

  function onPointerDown(e: PointerEvent) {
    viewport.setPointerCapture(e.pointerId)
    pointers.set(e.pointerId, local(e))
    prev = centroid()
  }

  function onPointerMove(e: PointerEvent) {
    if (!pointers.has(e.pointerId)) return
    pointers.set(e.pointerId, local(e))
    const now = centroid()
    if (prev) {
      if (pointers.size >= 2 && prev.dist > 0 && now.dist > 0) {
        const next = clamp(scale * (now.dist / prev.dist), MIN_SCALE, MAX_SCALE)
        const ratio = next / scale
        scale = next
        setPan(next, now.x - (prev.x - tx) * ratio, now.y - (prev.y - ty) * ratio)
      } else {
        setPan(scale, tx + (now.x - prev.x), ty + (now.y - prev.y))
      }
    }
    prev = now
  }

  function onPointerUp(e: PointerEvent) {
    const point = pointers.get(e.pointerId)
    pointers.delete(e.pointerId)
    // A lifted finger moves the centroid; re-seat it so the remaining finger doesn't read
    // the jump as a pan.
    prev = pointers.size > 0 ? centroid() : null

    if (!point || e.type === 'pointercancel') return
    const t = Date.now()
    if (
      lastTap &&
      t - lastTap.t < DOUBLE_TAP_MS &&
      Math.hypot(point.x - lastTap.x, point.y - lastTap.y) < DOUBLE_TAP_SLOP_PX
    ) {
      lastTap = null
      // Zooms in, wrapping back to fit at the top — otherwise a touch-only user at MAX_SCALE
      // has no way back except a pinch.
      if (scale >= MAX_SCALE - 0.001) zoomAt(MIN_SCALE, point.x, point.y)
      else zoomAt(scale * DOUBLE_TAP_FACTOR, point.x, point.y)
      return
    }
    lastTap = { t, x: point.x, y: point.y }
  }

  function onWheel(e: WheelEvent) {
    e.preventDefault()
    const p = local(e)
    zoomAt(scale * (e.deltaY < 0 ? WHEEL_FACTOR : 1 / WHEEL_FACTOR), p.x, p.y)
  }

  // Close on Escape for web/keyboard users.
  $effect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // A rotation or resize changes the viewport, and with it the pan bounds.
  $effect(() => {
    const onResize = () => setPan(scale, tx, ty)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  })
</script>

<div
  role="dialog"
  aria-modal="true"
  aria-label="MTA subway map"
  style="
    position: fixed; inset: 0;
    padding-bottom: {safeBottom()};
    z-index: 2000;
    background-color: {Colors.ground};
    display: flex; flex-direction: column;
  "
>
  <!-- Full-bleed overlay, so it owns its own safe area top and bottom (the header pads the
       status bar; the padding above keeps the framed map off the navigation bar). z-index
       sits above the detail sheets (VehicleSheet 1000 / route sheets 201) so the map covers
       the whole UI while open. -->

  <!-- Header: mirrors the sheet headers used elsewhere. -->
  <div
    style="
      flex-shrink: 0;
      display: flex; align-items: center; justify-content: space-between;
      padding: {safeTop(12)} 16px 12px;
      border-bottom: 1px solid {Colors.border};
      background-color: {Colors.ground};
    "
  >
    <span style={panelTitle(18)}>MTA subway map</span>
    <Button variant="text" size="sm" iconOnly onclick={onClose} aria-label="Close map">
      ✕
    </Button>
  </div>

  <!-- Interactive image viewer. -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    bind:this={viewport}
    onpointerdown={onPointerDown}
    onpointermove={onPointerMove}
    onpointerup={onPointerUp}
    onpointercancel={onPointerUp}
    onwheel={onWheel}
    style="
      flex: 1; min-height: 0; position: relative; overflow: hidden;
      {panelFrame()}
      touch-action: none; cursor: {scale > 1 ? 'grab' : 'default'};
    "
  >
    <div
      style="
        width: 100%; height: 100%;
        display: flex; align-items: center; justify-content: center;
        transform-origin: 0 0;
        transform: translate({tx}px, {ty}px) scale({scale});
      "
    >
      <img
        src={IMG_PATH}
        alt="MTA New York City subway map"
        draggable="false"
        onload={() => setPan(scale, tx, ty)}
        style="
          max-width: 100%; max-height: 100%;
          object-fit: contain;
          user-select: none; -webkit-user-select: none;
        "
      />
    </div>
  </div>
</div>
